import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { useMutation } from '@apollo/client';
import { Button } from '@companycam/slab-web';

import SnippetSchema from './formSchema';
import toast from 'helpers/toast';
import { translate } from 'translations/i18n';
import { UPDATE_REPORT_TEXT_SNIPPET } from '../gql/snippets.graphql';

import Form from './Form';

const FormHeader = ({ handleSelectDelete }) => (
  <React.Fragment>
    <h3>{translate('snippetEditor.edit.header')}</h3>
    <div>
      <Button
        color="destroy"
        size="small"
        onMouseDown={handleSelectDelete}
        style={{ marginRight: 8 }}
      >
        {translate('common.delete')}
      </Button>
      <Button
        color="primary"
        size="small"
        type="submit"
        onMouseDown={(e) => {
          e.preventDefault();
        }} // Allows button click and onBlur at the same time
      >
        {translate('snippetEditor.edit.header')}
      </Button>
    </div>
  </React.Fragment>
);

FormHeader.propTypes = {
  handleSelectDelete: PropTypes.func.isRequired,
};

const Edit = ({ snippet, insertSnippet, handleSelectDelete }) => {
  const [snippetContent, setSnippetContent] = useState(snippet.content);
  const [snippetTitle, setSnippetTitle] = useState(snippet.title);
  const [snippetSaved, setSnippetSaved] = useState(false);

  const [updateSnippet, { loading }] = useMutation(UPDATE_REPORT_TEXT_SNIPPET, {
    onCompleted: (result) => {
      setSnippetSaved(true);
      setSnippetContent(
        result.reportTextSnippetUpdate.reportsTextSnippet.content,
      );
      setSnippetTitle(result.reportTextSnippetUpdate.reportsTextSnippet.title);
      toast.success(translate('snippetEditor.edit.editSuccessMsg'));
    },
    onError: () => {
      toast.error(translate('snippetEditor.edit.editErrorMsg'));
    },
  });

  useEffect(() => {
    setSnippetTitle(snippet.title);
    setSnippetContent(snippet.content);
  }, [snippet]);

  const handleUseSnippet = () => {
    // updateSnippet if user inserts snippet directly, without unfocusing from editor.
    if (!loading && !snippetSaved) {
      const input = {
        variables: {
          input: {
            id: snippet.id,
            title: snippetTitle,
            content: snippetContent,
          },
        },
      };

      updateSnippet(input);
    }

    insertSnippet(null, snippetContent);
  };

  const handleUpdateSnippet = async (values) => {
    const { title, content } = values;

    const input = {
      variables: {
        input: {
          id: snippet.id,
          title,
          content,
        },
      },
    };

    updateSnippet(input);
  };

  const validation = SnippetSchema;

  return (
    <Formik
      initialValues={snippet}
      onSubmit={handleUseSnippet}
      validationSchema={validation}
      enableReinitialize
    >
      {(formikProps) => {
        // Set temporary changed snippet before updateSnippet mutation returns response
        // Used when user inserts snippet before mutation is complete
        setSnippetContent(formikProps.values.content);
        setSnippetTitle(formikProps.values.title);

        return (
          <Form
            {...formikProps}
            originalTags={snippet.tags.nodes}
            originalContent={snippet.content}
            handleUpdateSnippet={handleUpdateSnippet}
            clearSavedFlag={() => setSnippetSaved(false)}
          >
            <FormHeader handleSelectDelete={handleSelectDelete} />
          </Form>
        );
      }}
    </Formik>
  );
};

Edit.propTypes = {
  snippet: PropTypes.object,
  insertSnippet: PropTypes.func,
  handleSelectDelete: PropTypes.func,
};

export default Edit;
